// src/components/communications/SMSThread.jsx
// iMessage-style SMS conversation thread
import React, { useState, useRef, useEffect } from 'react';
import { Send, CheckCheck, Clock, AlertCircle, Smile, Paperclip, FolderPlus, Check, Loader2 } from 'lucide-react';
import { csrfHeadersFor } from '../../api';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function SMSThread({ messages, clientId, clientName, clientPhone, onMessageSent }) {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [savingMedia, setSavingMedia] = useState({});
  const inputRef = useRef(null);

  const handleSaveToDocuments = async (commId, mediaIndex) => {
    const key = `${commId}-${mediaIndex}`;
    setSavingMedia(prev => ({ ...prev, [key]: 'saving' }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/uploads/save-from-comm/${commId}/${mediaIndex}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}), ...csrfHeadersFor('POST') },
        credentials: 'include',
        body: JSON.stringify({ label: 'MMS Attachment' })
      });
      setSavingMedia(prev => ({ ...prev, [key]: res.ok ? 'saved' : 'error' }));
    } catch {
      setSavingMedia(prev => ({ ...prev, [key]: 'error' }));
    }
  };

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/comms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...csrfHeadersFor('POST') },
        credentials: 'include',
        body: JSON.stringify({
          clientId,
          type: 'sms',
          body: newMessage.trim(),
          direction: 'outbound'
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to send message');
      }

      setNewMessage('');
      // Mark all inbound messages as read since agent responded
      fetch(`${API_BASE}/comms/mark-read/${clientId}/sms`, { method: 'POST', credentials: 'include', headers: { ...csrfHeadersFor('POST') } }).catch(() => {});
      onMessageSent?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const diffDays = Math.round((today - msgDay) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday ' + date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' }) + ' ' + date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
  };

  const getDeliveryStatus = (msg) => {
    const meta = msg.metadata || msg.meta || {};
    const status = meta.deliveryStatus || meta.status;

    if (status === 'delivered') return { icon: CheckCheck, color: 'text-blue-500', label: 'Delivered' };
    if (status === 'sent') return { icon: CheckCheck, color: 'text-gray-400', label: 'Sent' };
    if (status === 'failed') return { icon: AlertCircle, color: 'text-red-500', label: 'Failed' };
    if (status === 'queued' || status === 'sending') return { icon: Clock, color: 'text-gray-400', label: 'Sending' };
    return { icon: CheckCheck, color: 'text-gray-300', label: '' };
  };

  // Group messages by date
  const groupedMessages = [];
  let currentDate = null;

  messages.forEach(msg => {
    const msgDate = new Date(msg.created_at).toDateString();
    if (msgDate !== currentDate) {
      currentDate = msgDate;
      groupedMessages.push({ type: 'date', date: msg.created_at });
    }
    groupedMessages.push({ type: 'message', ...msg });
  });

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-gray-100 to-gray-50">
      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto flex flex-col-reverse px-4 py-6">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <MessageBubbleIcon className="w-16 h-16 mb-4 text-gray-300" />
            <p className="text-lg font-medium">No messages yet</p>
            <p className="text-sm">Send a message to start the conversation</p>
          </div>
        ) : (
          <div className="space-y-1">
          {groupedMessages.map((item, idx) => {
            if (item.type === 'date') {
              return (
                <div key={`date-${idx}`} className="flex justify-center py-4">
                  <span className="px-4 py-1 bg-gray-200 text-gray-600 text-xs font-medium rounded-full">
                    {formatDateHeader(item.date)}
                  </span>
                </div>
              );
            }

            const msg = item;
            const isOutbound = msg.direction === 'outbound' || msg.direction === 'out';
            const status = getDeliveryStatus(msg);

            return (
              <div
                key={msg.id || idx}
                className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-2`}
              >
                <div className={`max-w-[75%] ${isOutbound ? 'order-2' : ''}`}>
                  {/* Message Bubble */}
                  <div
                    className={`rounded-2xl shadow-sm overflow-hidden ${
                      isOutbound
                        ? 'bg-amber-500 text-white rounded-br-md'
                        : 'bg-white text-gray-900 rounded-bl-md border border-gray-100'
                    }`}
                  >
                    {/* MMS Media */}
                    {msg.metadata?.mediaUrls?.length > 0 && (
                      <div className="flex flex-col gap-1">
                        {msg.metadata.mediaUrls.map((media, mIdx) => {
                          const isTwilio = media.url?.startsWith('https://api.twilio.com/');
                          const src = isTwilio ? `${API_BASE}/uploads/twilio-media/${msg.id}/${mIdx}` : media.url;
                          const saveKey = `${msg.id}-${mIdx}`;
                          const saveState = savingMedia[saveKey];
                          return (
                            <div key={mIdx} className="relative group">
                              <a href={src} target="_blank" rel="noopener noreferrer">
                                <img
                                  src={src}
                                  alt="MMS attachment"
                                  className="max-w-full rounded-t-2xl cursor-pointer hover:opacity-90 transition-opacity"
                                  style={{ maxHeight: '300px', objectFit: 'contain' }}
                                />
                              </a>
                              {!isOutbound && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSaveToDocuments(msg.id, mIdx); }}
                                  disabled={saveState === 'saving' || saveState === 'saved'}
                                  className={`absolute bottom-2 right-2 flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium shadow-md transition-all ${
                                    saveState === 'saved' ? 'bg-green-500 text-white'
                                    : saveState === 'saving' ? 'bg-gray-200 text-gray-500'
                                    : saveState === 'error' ? 'bg-red-500 text-white hover:bg-red-600'
                                    : 'bg-white/90 text-gray-700 hover:bg-white opacity-0 group-hover:opacity-100'
                                  }`}
                                  title="Save to client documents"
                                >
                                  {saveState === 'saving' ? <Loader2 className="w-3 h-3 animate-spin" />
                                    : saveState === 'saved' ? <><Check className="w-3 h-3" /> Saved</>
                                    : <><FolderPlus className="w-3 h-3" /> Save</>}
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {(msg.body || msg.message) && (
                      <p className="px-4 py-2.5 text-[15px] leading-relaxed whitespace-pre-wrap break-words">
                        {msg.body || msg.message}
                      </p>
                    )}
                  </div>

                  {/* Timestamp & Status */}
                  <div className={`flex items-center gap-1 mt-1 px-1 ${isOutbound ? 'justify-end' : 'justify-start'}`}>
                    <span className="text-[11px] text-gray-400">
                      {formatTime(msg.created_at)}
                    </span>
                    {isOutbound && (
                      <status.icon className={`w-3.5 h-3.5 ${status.color}`} />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mx-4 mb-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            &times;
          </button>
        </div>
      )}

      {/* Compose Area */}
      <div className="p-3 sm:p-4 bg-white border-t border-gray-200">
        <div className="flex items-end gap-2 sm:gap-3">
          {/* Input Container */}
          <div className="flex-1 relative">
            <textarea
              ref={inputRef}
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              rows={1}
              className="w-full px-4 py-3 pr-12 bg-gray-100 border-0 rounded-2xl resize-none focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white transition-all text-[15px] max-h-32"
              style={{ minHeight: '48px' }}
              disabled={sending}
            />
            <button className="absolute right-3 bottom-3 text-gray-400 hover:text-gray-600 transition-colors">
              <Smile className="w-5 h-5" />
            </button>
          </div>

          {/* Send Button */}
          <button
            onClick={handleSend}
            disabled={!newMessage.trim() || sending}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
              newMessage.trim() && !sending
                ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            {sending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>

        {/* Character count for long messages */}
        {newMessage.length > 100 && (
          <p className="text-xs text-gray-400 mt-2 text-right">
            {newMessage.length} characters
          </p>
        )}
      </div>
    </div>
  );
}

function formatDateHeader(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today - msgDay) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
  return date.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
}

function MessageBubbleIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
